import { useEffect, useState } from "react";
import { Alert, Button, Grid, Tag, Typography } from "antd";
import moment from "moment";
import { t } from "i18next";
import DatePicker from "../../../core/components/DatePicker";
import ApiService from "../../../core/service/ApiService";
import Helper from "../../../core/Helper";
import { CalendarProvider, useCalendar } from "./CalendarContext";
import Calendar from "./Calendar";

/**
 * Tek bir birimin günlük takvimini gösterip zaman aralığı seçtiren gömülü bileşen.
 * Randevu saati değiştirilirken tarih alanına elle yazmak yerine, o birimin öncesindeki/
 * sonrasındaki randevular görülerek boş aralık seçilebilsin diye var.
 *
 * Takvim SAYFASINDAN farkı: birim seçimi dışarıdan sabitlenir ve kullanıcının kalıcı birim
 * tercihini (localStorage) ezmez — CalendarProvider persistSelectedUnits={false} ile kurulur.
 * Seçim kaydetmez; yalnızca onSelect ile aralığı geri verir, kaydetme çağıranın işidir.
 */
function SlotPickerInner({ unitName, initialTime, onSelect, height }) {
    const { filter, setUnits, setDate } = useCalendar();
    const screens = Grid.useBreakpoint();
    const isNarrow = !screens.md;
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        ApiService.Unit.GetUnitList().then(res => {
            if (res.success)
                setUnits(res.data);
            setLoaded(true);
        });
    }, []);

    // Birim listesi kullanıcının yetkisine göre geliyor; randevunun birimi listede yoksa
    // takvim boş kalırdı — sebebini açıkça söylüyoruz.
    const unitAvailable = (filter.selectedUnits || []).length > 0;

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 8
                }}
            >
                <Tag color="blue" style={{ margin: 0 }}>{unitName || t('Unit')}</Tag>
                <DatePicker
                    locale={Helper.Date.getLocale()}
                    value={filter.date}
                    format={'DD/MM/yyyy, dddd'}
                    style={{ minWidth: 190, flex: isNarrow ? 1 : undefined }}
                    onChange={(val) => val && setDate(val)}
                    allowClear={false}
                    inputReadOnly
                />
                <Button onClick={() => setDate(moment(filter.date).add(-1, 'days'))}>{'<'}</Button>
                <Button onClick={() => setDate(moment(filter.date).add(1, 'days'))}>{'>'}</Button>
                <Button type={'primary'} onClick={() => setDate(moment())}>{t('Today')}</Button>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('Click an empty slot or drag a range')}
                </Typography.Text>
            </div>

            {loaded && !unitAvailable ? (
                <Alert
                    type="warning"
                    showIcon
                    message={t('The unit of this appointment is not available in your unit list.')}
                />
            ) : (
                <Calendar
                    pickMode={true}
                    time={initialTime}
                    height={height}
                    onSelect={onSelect}
                />
            )}
        </div>
    );
}

export default function AppointmentSlotPicker({ unitId, unitName, date, onSelect, height }) {
    // Yeni randevu akışında birim id'si URL'den string olarak geliyor; birim listesi ise
    // sayısal id taşıyor. Karşılaştırmalar (===) tutsun diye normalize ediliyor.
    const numericUnitId = unitId != null && unitId !== '' ? Number(unitId) : null;
    const startMoment = date ? moment(date) : moment();

    return (
        <CalendarProvider
            initialDate={startMoment}
            initialSelectedUnits={numericUnitId != null && !Number.isNaN(numericUnitId) ? [numericUnitId] : []}
            persistSelectedUnits={false}
        >
            <SlotPickerInner
                unitName={unitName}
                initialTime={startMoment.toDate()}
                onSelect={onSelect}
                height={height}
            />
        </CalendarProvider>
    );
}
